import { redirect, type LoaderFunctionArgs } from 'react-router-dom'
import { tokenManager } from '@/lib/tokenManager'
import { PLATFORM_TENANT_ID, type PermissionCode } from './permissions'
import { SMB_ONLY } from '@/config/productScope'

/**
 * Compute the encoded `next=` value for a redirect-to-login.
 *
 * Critical: read the DESTINATION URL from the loader's request, NOT
 * `window.location`. React Router fires the loader for the next route
 * BEFORE it commits the URL change, so `window.location` still points
 * at the source URL. If the source happened to be `/login?next=X`, we
 * would capture that whole string and prepend yet another `/login?next=`
 * around it - every click would add one nesting layer. That bug
 * produced URLs like:
 *   /login?next=%2Flogin%3Fnext%3D%252Flogin%253Fnext%253D...
 * `args.request.url` is the URL React Router is navigating to, which is
 * what `next` should resolve to after a successful sign-in.
 */
function buildNextFromArgs(args: LoaderFunctionArgs): string {
  try {
    const u = new URL(args.request.url)
    return encodeURIComponent(u.pathname + u.search)
  } catch {
    return encodeURIComponent('/app')
  }
}

/**
 * React Router v6 loader helper - short-circuits the route before the
 * page renders so users never see a flash-of-unauthorized content.
 *
 *   <Route path="/workflows" element={<WorkflowsPage/>}
 *          loader={requirePermission('workflows.view')} />
 */
export function requireAuth() {
  return (args: LoaderFunctionArgs) => {
    if (tokenManager.isExpired() || !tokenManager.get()) {
      throw redirect(`/login?next=${buildNextFromArgs(args)}`)
    }
    // Platform-tenant (Bokapsys staff) accounts never see customer pages.
    // Bounce them to the staff console - matches the legacy surface split.
    const user = tokenManager.getUser()
    if (user && user.tenantId === PLATFORM_TENANT_ID) {
      throw redirect('/platform-admin')
    }
    return null
  }
}

export function requirePermission(code: PermissionCode | string) {
  return (args: LoaderFunctionArgs) => {
    const user = tokenManager.getUser()
    if (!user || tokenManager.isExpired()) {
      throw redirect(`/login?next=${buildNextFromArgs(args)}`)
    }
    // Platform-tenant super_admin bypass.
    if (user.role === 'super_admin' && user.tenantId === PLATFORM_TENANT_ID) return null
    if (!user.permissions.includes(code)) throw redirect('/403')
    return null
  }
}

export function requireSmbScoped(code: PermissionCode | string) {
  const base = requirePermission(code)
  return (args: LoaderFunctionArgs) => {
    if (SMB_ONLY) throw redirect('/403')
    return base(args)
  }
}

export function requirePlatformAccess() {
  return (_args: LoaderFunctionArgs) => {
    const user = tokenManager.getUser()
    if (!user || tokenManager.isExpired()) {
      throw redirect('/admin-login')
    }
    if (user.tenantId !== PLATFORM_TENANT_ID) throw redirect('/403')
    return null
  }
}
